import React, { ReactNode } from 'react';

import { createPortal } from 'react-dom';

import cn from 'classnames';

import CheckIcon from 'design/Icons/CheckIcon';
import InfoLetterIcon from 'design/Icons/InfoLetterIcon';
import WarningIcon from 'design/Icons/WarningIcon';

export type DialogType = 'warning' | 'success' | 'alert';

interface ConfirmationDialogProps {
  open: boolean;
  title?: string;
  message: ReactNode;
  onDismiss: () => void;
  onConfirm: () => void;
  onThirdAction?: () => void;
  confirmBtnText?: string;
  cancelBtnText?: string;
  thirdBtnText?: string;
  type?: DialogType;
}

const ConfirmationDialog = ({
  title,
  open = false,
  onDismiss,
  onConfirm,
  message,
  confirmBtnText = 'Confirm',
  cancelBtnText = 'Cancel',
  type = 'warning',
  thirdBtnText,
  onThirdAction,
}: ConfirmationDialogProps) => {
  if (!open) return null;

  if (type === 'alert') {
    return createPortal(
      <div className="alert-confirmation-area">
        <div
          className="alert alert-confirmation alert-dismissible fade show alert-warning"
          role="alert"
        >
          <div className="alert-icon">
            <span>
              <InfoLetterIcon />
            </span>
          </div>

          <div className="alert-message">{message}</div>
          <div className="alert-actions">
            <button type="button" className="btn p-btn-warning" onClick={onDismiss}>
              {confirmBtnText}
            </button>
          </div>
        </div>
      </div>,
      document.getElementById('alert-confirmation-area-root')!,
    );
  }

  return createPortal(
    <div className="alert-confirmation-area">
      <div
        className={cn('alert alert-confirmation alert-dismissible fade show', {
          'alert-danger': type === 'warning',
          'alert-info': type === 'success',
        })}
        role="alert"
      >
        <div className="alert-icon">
          <span>{type === 'warning' ? <WarningIcon /> : <CheckIcon fill="white" />}</span>
        </div>

        <div className="alert-message">{message}</div>
        <div className="alert-actions">
          <button
            type="button"
            className={cn('btn', {
              'btn-danger': type === 'warning',
              'btn-primary': type === 'success',
            })}
            onClick={onConfirm}
          >
            {confirmBtnText}
          </button>

          {thirdBtnText && onThirdAction && (
            <button
              type="button"
              className={cn('btn', {
                'btn-danger': type === 'warning',
                'btn-primary': type === 'success',
              })}
              onClick={onThirdAction}
            >
              {thirdBtnText}
            </button>
          )}

          <button
            type="button"
            className="btn"
            data-dismiss="alert"
            aria-label="Close"
            onClick={onDismiss}
          >
            {cancelBtnText}
          </button>
        </div>
      </div>
    </div>,
    document.getElementById('alert-confirmation-area-root')!,
  );
};

export default ConfirmationDialog;
